import { WarningEvent } from '@sas-dvr/ltjs-commons';
import { PrintSetup } from '@sas-dvr/ltjs-transport';
import {
  PrintOrientation,
  PrintFitOption,
  PaperType,
  PrintTableOfContentsDisplayOption,
} from '@sas-dvr/ltjs-transport';
import { PrintEvent, ReportSession } from '@sas-dvr/ltjs-vav';

/**
 * @public
 */
export interface ExportPDFOptions {
  paperSize?:
    | 'letter'
    | 'legal'
    | 'ledger'
    | 'A3'
    | 'A4'
    | { width: number; height: number; units?: 'inches' | 'centimeters' };
  orientation?: 'landscape' | 'portrait';
  margin?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    units?: 'inches' | 'centimeters';
  };
  showPageNumbers?: boolean;
  showEmptyRowsAndColumns?: boolean;
  includeTableOfContents?: boolean;
  includeAppendix?: boolean;
  includeComments?: boolean;
  includeDetailsTables?: boolean;
  expandClippedContent?: boolean;
  includeCoverPage?: boolean;
  coverPageText?: string;
  includedReportObjects?: string[];
  enablePdfAccessibleTags?: boolean;
}

export const getPrintSetup = (options: ExportPDFOptions) => {
  const printSetup = new PrintSetup();
  const defaultOptions = {
    paperSize: 'letter',
    orientation: 'landscape',
    margin: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25, units: 'inches' },
    showPageNumbers: true,
    showEmptyRowsAndColumns: false,
    includeTableOfContents: false,
    includeAppendix: true,
    includeComments: false,
    includeDetailsTables: false,
    expandClippedContent: false,
    includeCoverPage: true,
    coverPageText: '',
    includedReportObjects: [],
    enablePdfAccessibleTags: false,
  };

  const exportPDFOptions = { ...defaultOptions, ...options };
  exportPDFOptions.margin = { ...defaultOptions.margin, ...options.margin };
  const marginUnit = exportPDFOptions.margin.units.toUpperCase() === 'CENTIMETERS' ? 'cm' : 'in';
  printSetup.setMarginTop(`${exportPDFOptions.margin.top} ${marginUnit}`);
  printSetup.setMarginBottom(`${exportPDFOptions.margin.bottom} ${marginUnit}`);
  printSetup.setMarginLeft(`${exportPDFOptions.margin.left} ${marginUnit}`);
  printSetup.setMarginRight(`${exportPDFOptions.margin.right} ${marginUnit}`);
  printSetup.setShowPageNumbers(exportPDFOptions.showPageNumbers);
  printSetup.setShowEmptyRowsAndColumns(exportPDFOptions.showEmptyRowsAndColumns);
  printSetup.setShowPrintedTableOfContents(exportPDFOptions.includeTableOfContents);
  printSetup.setShowAppendix(exportPDFOptions.includeAppendix);
  printSetup.setShowCoverPage(exportPDFOptions.includeCoverPage);
  printSetup.setCoverPageText(exportPDFOptions.coverPageText);
  printSetup.setTableOfContentsDisplay(PrintTableOfContentsDisplayOption.THUMBNAILS);
  printSetup.setEnablePdfAccessibleTags(exportPDFOptions.enablePdfAccessibleTags);

  const orientationOption = exportPDFOptions.orientation.toUpperCase();
  const orientation = ['LANDSCAPE', 'PORTRAIT'].includes(orientationOption)
    ? orientationOption
    : 'LANDSCAPE';
  printSetup.setOrientation(PrintOrientation[orientation as keyof typeof PrintOrientation]);

  const reportElementsString = exportPDFOptions.includedReportObjects.join(' ');
  printSetup.setReportElements(reportElementsString);

  if (typeof exportPDFOptions.paperSize === 'object') {
    printSetup.setPaperType(PaperType.CUSTOM);
    const paperUnit =
      exportPDFOptions.paperSize.units?.toUpperCase() === 'CENTIMETERS' ? 'cm' : 'in';
    printSetup.setPaperWidth(`${exportPDFOptions.paperSize.width} ${paperUnit}`);
    printSetup.setPaperHeight(`${exportPDFOptions.paperSize.height} ${paperUnit}`);
  } else {
    const optionsSize = exportPDFOptions.paperSize.toUpperCase();
    const paperSize = ['LETTER', 'LEGAL', 'LEDGER', 'A3', 'A4'].includes(optionsSize)
      ? optionsSize
      : 'LETTER';
    printSetup.setPaperType(PaperType[paperSize as keyof typeof PaperType]);
  }

  if (exportPDFOptions.expandClippedContent === true) {
    printSetup.setFitOption(PrintFitOption.EXPAND_CLIPPED_CONTENT_IN_APPENDIX);
  } else {
    printSetup.setFitOption(PrintFitOption.NONE);
  }

  const appendixDisplay = [];
  if (exportPDFOptions.includeComments) {
    appendixDisplay.push('comments');
  }
  if (exportPDFOptions.includeDetailsTables) {
    appendixDisplay.push('supplementalTables');
  }
  printSetup.setAppendixDisplay(appendixDisplay.join(' '));

  return printSetup;
};

export const exportPDF = async (
  reportSession: ReportSession,
  options?: ExportPDFOptions
): Promise<string> => {
  const printSetup = getPrintSetup(options ? options : {});
  reportSession.printReport(printSetup);
  return new Promise((resolve, reject) => {
    reportSession?.attachEventOnce(PrintEvent.PRINT_COMPLETED, (event) => {
      if (!(event instanceof PrintEvent)) {
        throw new Error('Unable to export PDF');
      }
      const url = event.getPDFLocation();
      if (url) {
        resolve(url);
      } else {
        return reject('URL not found');
      }
    });
    reportSession?.attachEventOnce(WarningEvent.GENERIC, (event) => {
      return reject((event as WarningEvent).getWarnings()?.[0]?.getMessage());
    });
  });
};
